import { forwardRef, useEffect, useState } from "react";
import {
    Filter,
    FunctionField,
    TextField,
    TextInput,
    usePermissions,
    useResourceContext,
    useTranslate,
    NumberField,
    ReferenceField,
    useLocaleState,
    BooleanField,
    useGetList,
    useListContext,
    useRefresh,
} from "react-admin";
import { Chip } from "@mui/material";
import { DefaultPagination } from "shared/src/components/DefaultPagination";
import { ToolbarWithHistory } from "shared/src/components/ToolbarWithHistory";
import { DatagridWithHistory } from "shared/src/components/DatagridWithHistory";
import { queryToAttribute } from "shared/src/utils/decodeQuery";
import { AccessRightsError } from "shared/src/components/AccessRightsError";
import { RaDateField } from "shared/src/components/fields/Ra/RaDateField";
import { RaListView } from "shared/src/components/views/RaListView";
import { FileManagerContextProvider } from "./fileInfoManagement/FileManagerContext";
import { FileInformationManager } from "./fileInfoManagement/FileInformationManager";
import { ExtractButton } from "./ExtractButton";

// Tooltip needs a DOM element even when the false icon is visually empty.
const EmptyArrivalIcon = forwardRef(function EmptyArrivalIcon({ fontSize, ...props }, ref) {
    return <span {...props} ref={ref} />;
});

const ArrivalListFilter = (props) => {
    const translate = useTranslate();
    return (
        <Filter {...props}>
            <TextInput
                title={translate("equip.purchaseOrderNumber")}
                label="equip.purchaseOrderNumber"
                source="purchaseOrderNumber"
                alwaysOn
            />
        </Filter>
    );
};

export const ArrivalList = (props) => {
    const resource = useResourceContext();
    const attribute = queryToAttribute();
    const [locale] = useLocaleState();
    const { isLoading, permissions } = usePermissions();
    const [arrival, setArrival] = useState(null);

    if (isLoading) {
        return <></>;
    } else if (!permissions.vote({ resource, attribute })) {
        return <AccessRightsError />;
    }
    return (
        <>
            <PreloadedExtraction arrival={arrival} setArrival={setArrival} />
            <RaListView
                {...props}
                actions={<ToolbarWithHistory create={true} export={true} extractButton={<ExtractButton />} />}
                filters={<ArrivalListFilter />}
                pagination={<DefaultPagination />}
                perPage={50}
                sort={{ field: "id", order: "DESC" }}
            >
                <RefreshRunningExtractions />
                <DatagridWithHistory
                    bulkActionButtons={false}
                    rowClick={(id, resource, record) => {
                        if (!record.product?.id && !record.quantity && record.mediaCount > 0) {
                            setArrival(record);
                            return false;
                        }
                        return "show";
                    }}
                >
                    <NumberField label="ID" source="id" />
                    <FunctionField
                        label="equip.extractionStatusLabel"
                        source="extractionStatus"
                        render={(record) => <ExtractionStatusChip status={record.extractionStatus} />}
                    />
                    <ReferenceField
                        label="equip.product"
                        source="product.id"
                        reference="products"
                        link={false}
                    >
                        <TextField source={`name[${locale}]`} />
                    </ReferenceField>
                    <ReferenceField
                        label="equip.supplier"
                        source="supplier.id"
                        reference="suppliers"
                        link={false}
                    >
                        <TextField source={"name"} />
                    </ReferenceField>
                    <BooleanField
                        source="mainArrival"
                        label="equip.mainArrival"
                        FalseIcon={EmptyArrivalIcon}
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    />
                    <RaDateField label="equip.orderDate" source="orderDate" />
                    <RaDateField label="equip.deliveryDate" source="deliveryDate" />
                    <TextField label="equip.purchaseOrderNumber" source="purchaseOrderNumber" />
                    <NumberField label="equip.quantity" source="quantity" />
                    <TextField label="equip.comment" source="comment" />
                </DatagridWithHistory>
            </RaListView>
        </>
    );
};

const RefreshRunningExtractions = () => {
    const { data } = useListContext();
    const refresh = useRefresh();
    const hasRunningExtraction = data?.some((record) =>
        ["queued", "processing"].includes(record.extractionStatus)
    );

    useEffect(() => {
        if (!hasRunningExtraction) return;

        const interval = setInterval(refresh, 10000);

        return () => clearInterval(interval);
    }, [hasRunningExtraction, refresh]);

    return null;
};

const ExtractionStatusChip = ({ status }) => {
    const translate = useTranslate();
    if (!status || status === "none") {
        return <></>;
    }

    const color = {
        queued: "default",
        processing: "info",
        succeeded: "success",
        failed: "error",
    }[status] ?? "default";

    return (
        <Chip
            size="small"
            color={color}
            label={translate(`equip.extractionStatus.${status}`)}
        />
    );
};

const PreloadedExtraction = ({ arrival, setArrival }) => {
    const [media, setMedia] = useState(null);
    useGetList(
        "media",
        {
            filter: { arrival: arrival?.id },
            pagination: { page: 1, perPage: 100 },
        },
        {
            onSuccess: ({ data }) => {
                setMedia(data[0]);
            },
            enabled: !!arrival?.id,
        }
    );
    const handleCloseModal = () => {
        setArrival(null);
        setMedia(null);
    };

    return (
        arrival &&
        media && (
            <FileManagerContextProvider
                arrival={arrival}
                media={media}
                isUploading={false}
                onClose={handleCloseModal}
            >
                <FileInformationManager
                    media={media}
                    isUploading={false}
                    onClose={handleCloseModal}
                />
            </FileManagerContextProvider>
        )
    );
};
